import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, MapPin, ShieldCheck, Syringe, Cpu, ArrowLeft, CheckCircle2, Clock, XCircle, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { NotifyMe } from "../components/NotifyMe";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const APP_FIELD = "w-full bg-eon-bg border border-eon-border px-3 py-2.5 text-sm focus:border-eon-gold outline-none";

function DogGallery({ dog, images, active, setActive }) {
  return (
    <div>
      <div className="relative aspect-[4/5] overflow-hidden border border-eon-border">
        <img src={images[active]} alt={dog.name} className="w-full h-full object-cover" />
        {dog.is_eon_owned
          ? <span className="absolute top-4 left-4 eon-label bg-eon-gold text-white px-2 py-1">E.O.N Owned</span>
          : <span className="absolute top-4 left-4 eon-label bg-black/60 px-2 py-1">Third-Party Breeder</span>}
      </div>
      {images.length > 1 && (
        <div className="grid grid-cols-4 gap-3 mt-3">
          {images.map((im, i) => (
            <button key={im} onClick={() => setActive(i)} className={`aspect-square overflow-hidden border ${active === i ? "border-eon-gold" : "border-eon-border"}`}>
              <img src={im} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DogSpecs({ dog, onFav }) {
  return (
    <>
      <div className="eon-label">{dog.breed} · {dog.registration} Registered</div>
      <div className="flex items-start justify-between gap-4 mt-3">
        <h1 className="eon-heading text-4xl lg:text-5xl uppercase">{dog.name}</h1>
        <button onClick={onFav} data-testid={`fav-dog-${dog.id}`} className="w-11 h-11 flex items-center justify-center border border-eon-border hover:border-eon-gold hover:text-eon-gold transition-colors"><Heart size={18} /></button>
      </div>
      <div className="mt-4 flex items-center gap-4 text-sm text-eon-muted">
        <span className="capitalize">{dog.sex}</span><span>·</span><span>{dog.age}</span><span>·</span>
        <span className="flex items-center gap-1"><MapPin size={14} /> {dog.city}, {dog.state}</span>
      </div>
      <div className="mt-6 flex items-baseline gap-3">
        <span className="font-mono text-3xl text-eon-gold font-medium">${dog.price?.toLocaleString()}</span>
        <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 ${dog.status === "available" ? "bg-eon-gold text-white" : "bg-eon-elevated text-eon-text/70 border border-eon-border"}`}>{(dog.status || "available").replace("_", " ")}</span>
      </div>
      <p className="mt-6 text-eon-text/80 leading-relaxed">{dog.description}</p>

      <div className="grid grid-cols-2 gap-px bg-eon-border border border-eon-border mt-8">
        {[["Sire", dog.sire], ["Dam", dog.dam], ["Est. Weight", dog.weight], ["Registration", dog.registration]].map(([k, v]) => (
          <div key={k} className="bg-eon-bg p-4"><div className="eon-label">{k}</div><div className="text-sm mt-1">{v}</div></div>
        ))}
      </div>
      <div className="flex gap-5 mt-5 text-sm text-eon-text/80">
        {dog.vaccinated && <span className="flex items-center gap-2"><Syringe size={16} className="text-eon-gold" /> Vaccinated</span>}
        {dog.microchipped && <span className="flex items-center gap-2"><Cpu size={16} className="text-eon-gold" /> Microchipped</span>}
        {dog.dna_tested && <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-eon-gold" /> DNA tested</span>}
      </div>

      {dog.puppy_package?.length > 0 && (
        <div className="mt-6 border border-eon-gold/30 bg-eon-surface p-5" data-testid="puppy-package">
          <div className="eon-label mb-3">Included With This E.O.N Puppy</div>
          <ul className="grid sm:grid-cols-2 gap-2">
            {dog.puppy_package.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-eon-text/90">
                <CheckCircle2 size={16} className="text-eon-gold shrink-0 mt-0.5" /> {item}
              </li>
            ))}
          </ul>
          {dog.leaves_at && <p className="mt-4 text-xs text-eon-muted">South African breed · Puppies leave at {dog.leaves_at} · Registered {dog.registration}</p>}
        </div>
      )}
    </>
  );
}

function ApplicationDialog({ dog, deposit, appOpen, setAppOpen, appForm, setAppForm, submitApplication, submittingApp }) {
  return (
    <Dialog open={appOpen} onOpenChange={setAppOpen}>
      <DialogTrigger asChild>
        <button data-testid="dog-apply-btn"
          className="w-full bg-eon-gold text-white text-sm font-bold uppercase tracking-wider px-6 py-4 hover:bg-eon-goldhover transition-colors">
          Apply to Reserve · ${deposit.toLocaleString()} deposit
        </button>
      </DialogTrigger>
      <DialogContent className="bg-eon-surface border-eon-border text-eon-text max-h-[88vh] overflow-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Puppy Reservation Application</DialogTitle></DialogHeader>
        <p className="text-xs text-eon-muted -mt-2">Applying for <span className="text-eon-gold">{dog.name}</span>. Our team reviews every applicant to ensure the right home. Deposits are only collected after approval.</p>
        <form onSubmit={submitApplication} className="space-y-3 pt-2" data-testid="application-form">
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Full name" value={appForm.full_name} onChange={(e) => setAppForm({ ...appForm, full_name: e.target.value })} className={APP_FIELD} data-testid="app-fullname" />
            <input required type="email" placeholder="Email" value={appForm.email} onChange={(e) => setAppForm({ ...appForm, email: e.target.value })} className={APP_FIELD} data-testid="app-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Phone" value={appForm.phone} onChange={(e) => setAppForm({ ...appForm, phone: e.target.value })} className={APP_FIELD} data-testid="app-phone" />
            <input placeholder="City" value={appForm.city} onChange={(e) => setAppForm({ ...appForm, city: e.target.value })} className={APP_FIELD} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="State" value={appForm.state} onChange={(e) => setAppForm({ ...appForm, state: e.target.value })} className={APP_FIELD} />
            <select value={appForm.home_type} onChange={(e) => setAppForm({ ...appForm, home_type: e.target.value })} className={APP_FIELD} data-testid="app-hometype">
              <option value="house">House</option><option value="apartment">Apartment</option><option value="other">Other</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={appForm.experience} onChange={(e) => setAppForm({ ...appForm, experience: e.target.value })} className={APP_FIELD} data-testid="app-experience">
              <option value="first-time">First-time owner</option><option value="some">Some experience</option><option value="experienced">Experienced / large breed</option>
            </select>
            <select value={appForm.purpose} onChange={(e) => setAppForm({ ...appForm, purpose: e.target.value })} className={APP_FIELD} data-testid="app-purpose">
              <option value="companion">Companion</option><option value="working">Working / protection</option><option value="show">Show</option><option value="breeding">Breeding program</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={appForm.has_yard} onChange={(e) => setAppForm({ ...appForm, has_yard: e.target.checked })} data-testid="app-yard" /> I have a secure yard / outdoor space</label>
          <textarea rows={2} placeholder="Your household (family, children, other pets)" value={appForm.household} onChange={(e) => setAppForm({ ...appForm, household: e.target.value })} className={APP_FIELD} data-testid="app-household" />
          <textarea rows={3} placeholder="Why a Boerboel, and what are you looking for?" value={appForm.why_boerboel} onChange={(e) => setAppForm({ ...appForm, why_boerboel: e.target.value })} className={APP_FIELD} data-testid="app-why" />
          <select value={appForm.timeline} onChange={(e) => setAppForm({ ...appForm, timeline: e.target.value })} className={APP_FIELD} data-testid="app-timeline">
            <option value="ready now">Ready now</option><option value="1-3 months">1–3 months</option><option value="3-6 months">3–6 months</option><option value="just exploring">Just exploring</option>
          </select>
          <label className="flex items-start gap-2 text-xs text-eon-muted"><input type="checkbox" checked={appForm.agree_terms} onChange={(e) => setAppForm({ ...appForm, agree_terms: e.target.checked })} data-testid="app-agree" className="mt-0.5" /> I understand my application will be reviewed and that a reservation deposit is only due after approval.</label>
          <button type="submit" disabled={submittingApp} className="w-full bg-eon-gold text-white font-bold uppercase tracking-wider py-3.5 hover:bg-eon-goldhover transition-colors disabled:opacity-40" data-testid="app-submit">{submittingApp ? "Submitting..." : "Submit Application"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InquiryDialog({ dog, form, setForm, submitInquiry, sent }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="dog-inquire-btn"
          className="w-full border border-eon-border text-eon-text text-sm font-bold uppercase tracking-wider px-6 py-3.5 hover:border-eon-gold hover:text-eon-gold transition-colors">
          Ask a Question
        </button>
      </DialogTrigger>
      <DialogContent className="bg-eon-surface border-eon-border text-eon-text">
        <DialogHeader><DialogTitle className="font-display text-2xl">Contact {dog.is_eon_owned ? "E.O.N" : "Seller"}</DialogTitle></DialogHeader>
        {sent ? (
          <div className="py-8 text-center">
            <CheckCircle2 size={40} className="text-eon-gold mx-auto" />
            <p className="mt-4 text-eon-text/80">Your inquiry was sent. We'll reach out via email.</p>
          </div>
        ) : (
          <form onSubmit={submitInquiry} className="space-y-4 pt-2" data-testid="inquiry-form">
            <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-eon-bg border border-eon-border px-3 py-3 focus:border-eon-gold outline-none" data-testid="inquiry-name" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-eon-bg border border-eon-border px-3 py-3 focus:border-eon-gold outline-none" data-testid="inquiry-email" />
            <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-eon-bg border border-eon-border px-3 py-3 focus:border-eon-gold outline-none" data-testid="inquiry-phone" />
            <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-eon-bg border border-eon-border px-3 py-3 focus:border-eon-gold outline-none" data-testid="inquiry-message" />
            <button type="submit" className="w-full bg-eon-gold text-white font-bold uppercase tracking-wider py-3.5 hover:bg-eon-goldhover transition-colors" data-testid="inquiry-submit">Send Inquiry</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ApprovedReserve({ deposit, depositing, startDeposit }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-green-400" data-testid="app-status-approved"><CheckCircle2 size={16} /> Application approved — you may now reserve this puppy.</div>
      <button onClick={startDeposit} disabled={depositing} data-testid="dog-deposit-btn"
        className="w-full bg-eon-gold text-white text-sm font-bold uppercase tracking-wider px-6 py-4 hover:bg-eon-goldhover transition-colors disabled:opacity-40">
        {depositing ? "Redirecting..." : `Pay Reservation Deposit · $${deposit.toLocaleString()}`}
      </button>
    </div>
  );
}

function ApplicationState({ st, myApp }) {
  if (st === "reserved") return <div className="flex items-center gap-2 border border-eon-gold/30 bg-eon-surface p-4 text-sm text-green-400" data-testid="app-status-reserved"><CheckCircle2 size={16} /> Reserved — your deposit has been received. Our team will be in touch.</div>;
  if (st === "pending") return <div className="flex items-center gap-2 border border-eon-border bg-eon-surface p-4 text-sm text-yellow-400" data-testid="app-status-pending"><Clock size={16} /> Application under review. We'll email you once a decision is made.</div>;
  if (st === "waitlisted") return <div className="border border-eon-border bg-eon-surface p-4 text-sm text-yellow-400" data-testid="app-status-waitlisted"><span className="flex items-center gap-2"><ListChecks size={16} /> You're on the waitlist for this puppy.</span>{myApp?.admin_note && <p className="text-eon-muted mt-1">Note: {myApp.admin_note}</p>}</div>;
  if (st === "declined") return <div className="border border-eon-border bg-eon-surface p-4 text-sm text-red-400" data-testid="app-status-declined"><span className="flex items-center gap-2"><XCircle size={16} /> Application not approved at this time.</span>{myApp?.admin_note && <p className="text-eon-muted mt-1">Note: {myApp.admin_note}</p>}</div>;
  return null;
}

function ReservationBlock(props) {
  const { dog, user, nav, sold, st, myApp, deposit, depositing, startDeposit,
          appOpen, setAppOpen, appForm, setAppForm, submitApplication, submittingApp } = props;

  const renderPrimary = () => {
    if (dog.status === "coming_soon") {
      return (
        <div className="space-y-3" data-testid="waitlist-block">
          <div className="flex items-center gap-2 border border-eon-gold/30 bg-eon-surface p-4 text-sm text-eon-text/80">
            This E.O.N litter is coming soon. Join the waitlist for first-in-line reservation access.
          </div>
          <NotifyMe kind="dog" itemId={dog.id} itemName={dog.name} label="Join the Waitlist"
            triggerTestId="dog-waitlist-btn"
            className="w-full flex items-center justify-center gap-2 bg-eon-gold text-white text-sm font-bold uppercase tracking-wider px-6 py-4 hover:bg-eon-goldhover transition-colors" />
        </div>
      );
    }
    if (sold) {
      return <div className="flex items-center gap-2 border border-eon-border bg-eon-surface p-4 text-sm text-eon-muted" data-testid="dog-placed"><XCircle size={16} /> {dog.status === "reserved" ? "This puppy is already reserved." : "This puppy has been placed."}</div>;
    }
    if (!user) {
      return (
        <button onClick={() => nav("/login")} data-testid="reserve-signin-btn"
          className="w-full bg-eon-gold text-white text-sm font-bold uppercase tracking-wider px-6 py-4 hover:bg-eon-goldhover transition-colors">
          Sign in to Apply for Reservation
        </button>
      );
    }
    if (st === "approved") return <ApprovedReserve deposit={deposit} depositing={depositing} startDeposit={startDeposit} />;
    if (st) return <ApplicationState st={st} myApp={myApp} />;
    return <ApplicationDialog dog={dog} deposit={deposit} appOpen={appOpen} setAppOpen={setAppOpen}
      appForm={appForm} setAppForm={setAppForm} submitApplication={submitApplication} submittingApp={submittingApp} />;
  };

  return (
    <div className="mt-8 space-y-4" data-testid="reservation-block">
      {renderPrimary()}
      <InquiryDialog dog={dog} form={props.form} setForm={props.setForm} submitInquiry={props.submitInquiry} sent={props.sent} />
    </div>
  );
}

export default function DogDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [dog, setDog] = useState(null);
  const [active, setActive] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [depositing, setDepositing] = useState(false);
  const [myApp, setMyApp] = useState(null);
  const [appOpen, setAppOpen] = useState(false);
  const [submittingApp, setSubmittingApp] = useState(false);
  const [appForm, setAppForm] = useState({
    full_name: "", email: "", phone: "", city: "", state: "",
    home_type: "house", has_yard: true, household: "", experience: "some",
    purpose: "companion", why_boerboel: "", timeline: "1-3 months", agree_terms: false,
  });

  useEffect(() => {
    api.get(`/dogs/${id}`).then((r) => { setDog(r.data); setForm((f) => ({ ...f, message: `I'm interested in ${r.data.name} (${r.data.breed}).` })); });
  }, [id]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, name: user.name, email: user.email }));
      setAppForm((f) => ({ ...f, full_name: f.full_name || user.name, email: f.email || user.email }));
      api.get(`/applications/for-dog/${id}`).then((r) => setMyApp(r.data && r.data.id ? r.data : null)).catch(() => {});
    } else {
      setMyApp(null);
    }
  }, [user, id]);

  const submitInquiry = async (e) => {
    e.preventDefault();
    try {
      await api.post("/inquiries", { subject_type: dog.is_boerboel ? "boerboel_application" : "dog", subject_id: dog.id, ...form });
      setSent(true); toast.success("Inquiry sent to the seller");
    } catch { toast.error("Could not send inquiry"); }
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!appForm.agree_terms) { toast.error("Please agree to the reservation terms"); return; }
    setSubmittingApp(true);
    try {
      const { data } = await api.post("/applications", { dog_id: dog.id, ...appForm });
      setMyApp(data); setAppOpen(false);
      toast.success("Application submitted for review");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not submit application"); }
    finally { setSubmittingApp(false); }
  };

  const startDeposit = async () => {
    setDepositing(true);
    try {
      const { data } = await api.post("/payments/checkout", { kind: "deposit", dog_id: dog.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start checkout"); setDepositing(false); }
  };

  const fav = async () => {
    if (!user) { toast.error("Sign in to save favorites"); return; }
    const { data } = await api.post("/favorites", { item_type: "dog", item_id: dog.id });
    toast.success(data.favorited ? "Saved" : "Removed");
  };

  if (!dog) return <Layout><div className="max-w-[1400px] mx-auto px-5 py-32 text-eon-muted">Loading...</div></Layout>;
  const images = dog.images?.length ? dog.images : [dog.image];
  const deposit = dog.deposit_amount || Math.round(dog.price * 0.2);
  const sold = dog.status === "sold" || dog.status === "reserved";
  const st = myApp?.status;

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-8">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-eon-muted hover:text-eon-gold transition-colors mb-6"><ArrowLeft size={16} /> Back</button>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <DogGallery dog={dog} images={images} active={active} setActive={setActive} />

          <div>
            <DogSpecs dog={dog} onFav={fav} />

            <ReservationBlock
              dog={dog} user={user} nav={nav} sold={sold} st={st} myApp={myApp}
              deposit={deposit} depositing={depositing} startDeposit={startDeposit}
              appOpen={appOpen} setAppOpen={setAppOpen} appForm={appForm} setAppForm={setAppForm}
              submitApplication={submitApplication} submittingApp={submittingApp}
              form={form} setForm={setForm} submitInquiry={submitInquiry} sent={sent}
            />

            {dog.breeder && (
              <Link to={`/breeders/${dog.breeder.id}`} className="mt-8 flex items-center gap-4 eon-card p-4">
                <img src={dog.breeder.image} alt="" className="w-14 h-14 object-cover" />
                <div><div className="eon-label">Seller</div><div className="font-display text-lg font-bold">{dog.breeder.business_name}</div><div className="text-xs text-eon-muted">{dog.breeder.location}</div></div>
              </Link>
            )}
            <p className="mt-6 text-xs text-eon-muted leading-relaxed border-t border-eon-border pt-6">
              Disclosure: Third-party listings are the responsibility of the seller. E.O.N facilitates connections and does not guarantee non-E.O.N inventory. Review our <Link to="/trust-safety" className="text-eon-gold">Trust & Safety standards</Link>.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
